import DashboardHeader from "@/components/dashboard/DashboardHeader";
import DashboardLoading from "@/components/dashboard/DashboardLoading";
import { useIndexStats } from "@/hooks/useIndexStats";
import { useDashboardLayout } from "@/contexts/DashboardLayoutContext";
import DashboardCustomizer from "@/components/dashboard/DashboardCustomizer";
import { AnalyticsWidget } from "./widgets/AnalyticsWidget";
import { ClientStatusCardsWidget } from "./widgets/ClientStatusCardsWidget";
import { TopEmployeesWidget } from "./widgets/TopEmployeesWidget";
import { PackagePerformanceWidget } from "./widgets/PackagePerformanceWidget";
import { PackageKPIWidget } from "./widgets/PackageKPIWidget";
import { PackageFinancialAnalyticsWidget } from "./widgets/PackageFinancialAnalyticsWidget";
import { EmployeePackagePerformanceWidget } from "./widgets/EmployeePackagePerformanceWidget";
import { SortableWidget } from "./layout/SortableWidget";
import { ClientStatusChart } from "@/components/ui/interactive/InteractiveChart";
import { useClients } from "@/hooks/useClients";
import { useClientStatuses } from "@/hooks/useClientStatuses";
import { Suspense, memo, useMemo } from "react";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

// Memoized components for better performance
const MemoizedAnalyticsWidget = memo(AnalyticsWidget);
const MemoizedClientStatusCardsWidget = memo(ClientStatusCardsWidget);
const MemoizedClientStatusChart = memo(ClientStatusChart);
const MemoizedTopEmployeesWidget = memo(TopEmployeesWidget);
const MemoizedPackagePerformanceWidget = memo(PackagePerformanceWidget);
const MemoizedPackageKPIWidget = memo(PackageKPIWidget);
const MemoizedPackageFinancialAnalyticsWidget = memo(PackageFinancialAnalyticsWidget);
const MemoizedEmployeePackagePerformanceWidget = memo(EmployeePackagePerformanceWidget);

export const DashboardContent = () => {
  const {
    employees,
    stats,
    loading: statsLoading,
    error: statsError
  } = useIndexStats();

  const {
    data: clients = [],
    isLoading: clientsLoading
  } = useClients();
  
  const {
    data: clientStatuses = [],
    isLoading: statusesLoading
  } = useClientStatuses();
  
  const {
    widgets,
    reorderWidgets,
    isCustomizing
  } = useDashboardLayout();
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }), 
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const chartData = useMemo(() => {
    if (!clients.length || !clientStatuses.length) return [];
    return clientStatuses.map(status => ({
      status_name: status.name_ar,
      count: clients.filter(client => client.status === status.status_key).length,
      color: status.color
    }));
  }, [clients, clientStatuses]);

  if (statsLoading && !employees.length) {
    return <DashboardLoading />;
  }
  
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      reorderWidgets(active.id as string, over.id as string);
    }
  };
  
  const handleRetry = () => {
    window.location.reload();
  };
  
  const renderWidget = (widgetId: string) => {
    switch (widgetId) {
      case 'analytics-section':
        return (
          <ErrorBoundary fallback={<div className="p-4 text-center text-red-500">خطأ في تحميل التحليلات</div>}>
            <Suspense fallback={<div className="animate-pulse bg-muted/20 rounded-xl h-48 sm:h-64" />}>
              <MemoizedAnalyticsWidget 
                isLoading={statsLoading}
                error={statsError}
                onRetry={handleRetry}
              />
            </Suspense>
          </ErrorBoundary>
        );
      case 'client-status-cards':
        return (
          <ErrorBoundary fallback={<div className="p-4 text-center text-red-500">خطأ في تحميل بطاقات الحالة</div>}>
            <Suspense fallback={<div className="animate-pulse bg-muted/20 rounded-xl h-32 sm:h-48" />}>
              <MemoizedClientStatusCardsWidget 
                clients={clients}
                clientStatuses={clientStatuses}
                isLoading={clientsLoading || statusesLoading}
                error={null}
                onRetry={handleRetry}
              />
            </Suspense>
          </ErrorBoundary>
        );
      case 'client-status-chart':
        return (
          <ErrorBoundary fallback={<div className="p-4 text-center text-red-500">خطأ في تحميل الرسم البياني</div>}>
            <Suspense fallback={<div className="animate-pulse bg-muted/20 rounded-xl h-64 sm:h-96" />}>
              <MemoizedClientStatusChart data={chartData} />
            </Suspense>
          </ErrorBoundary>
        );
      case 'top-employees':
        return (
          <ErrorBoundary fallback={<div className="p-4 text-center text-red-500">خطأ في تحميل الموظفين</div>}>
            <Suspense fallback={<div className="animate-pulse bg-muted/20 rounded-xl h-64 sm:h-96" />}>
              <MemoizedTopEmployeesWidget 
                isLoading={statsLoading}
                error={statsError}
                onRetry={handleRetry}
              />
            </Suspense>
          </ErrorBoundary>
        );
      case 'package-performance':
        return (
          <ErrorBoundary fallback={<div className="p-4 text-center text-red-500">خطأ في تحميل أداء الباقات</div>}>
            <Suspense fallback={<div className="animate-pulse bg-muted/20 rounded-xl h-64 sm:h-96" />}>
              <MemoizedPackagePerformanceWidget />
            </Suspense>
          </ErrorBoundary>
        );
      case 'package-kpis':
        return (
          <ErrorBoundary fallback={<div className="p-4 text-center text-red-500">خطأ في تحميل مؤشرات الباقات</div>}>
            <Suspense fallback={<div className="animate-pulse bg-muted/20 rounded-xl h-64 sm:h-96" />}>
              <MemoizedPackageKPIWidget />
            </Suspense>
          </ErrorBoundary>
        );
      case 'package-financials':
        return (
          <ErrorBoundary fallback={<div className="p-4 text-center text-red-500">خطأ في تحميل التحليلات المالية للباقات</div>}>
            <Suspense fallback={<div className="animate-pulse bg-muted/20 rounded-xl h-64 sm:h-96" />}>
              <MemoizedPackageFinancialAnalyticsWidget />
            </Suspense>
          </ErrorBoundary>
        );
      case 'employee-package-performance':
        return (
          <ErrorBoundary fallback={<div className="p-4 text-center text-red-500">خطأ في تحميل أداء الموظفين في الباقات</div>}>
            <Suspense fallback={<div className="animate-pulse bg-muted/20 rounded-xl h-64 sm:h-96" />}>
              <MemoizedEmployeePackagePerformanceWidget />
            </Suspense>
          </ErrorBoundary>
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="w-full space-y-4 sm:space-y-6 px-2 sm:px-0" dir="rtl">
      <DashboardHeader />
      <DashboardCustomizer />
      
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={widgets.map(w => w.id)} strategy={verticalListSortingStrategy}>
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {widgets.filter(w => w.isVisible).map(widget => (
              <SortableWidget key={widget.id} id={widget.id} isCustomizing={isCustomizing}>
                {renderWidget(widget.id)}
              </SortableWidget>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
